/**
 * HVILKE KOLONNER DENNE LESEREN VIL SE — `/v1/me/table-prefs/:tableKey` (Del 3).
 *
 * =================================================================================================
 * KATALOGEN ER KODENS, VALGET ER BRUKERENS.
 *
 * Serveren lagrer en LISTE med nøkler og synlighet, i rekkefølge. Den validerer ikke at nøklene
 * finnes — det er klientens katalog som avgjør, og den endrer seg oftere enn basen.
 *
 * To regler gjør at en lagret preferanse overlever at katalogen endres:
 *
 *   · en nøkkel som ikke lenger finnes i katalogen IGNORERES ved lesing (ikke en feil — kolonnen
 *     ble fjernet, og brukeren skal ikke måtte rydde),
 *   · en ny kolonne i katalogen legges til SKJULT BAKERST (ikke synlig: en kolonne som dukker opp
 *     av seg selv i en tabell noen har stilt inn, er en tabell som flytter på seg).
 *
 * Begge hører hjemme i klienten, der katalogen er. Serveren er en boks å legge valget i.
 */
import { z } from 'zod';

/** Så mange kolonner kan en tabell ha før valget er en meny ingen leser. */
export const MAX_TABLE_COLUMNS = 40;

export const tableColumnPrefSchema = z
  .object({
    /** Katalogens nøkkel, ikke en visningstekst: «commissionBookingBp», ikke «Provisjon». */
    key: z.string().trim().min(1).max(60),
    visible: z.boolean(),
  })
  .meta({ id: 'TableColumnPref' });
export type TableColumnPref = z.infer<typeof tableColumnPrefSchema>;

export const tablePrefsDtoSchema = z
  .object({
    tableKey: z.string(),
    /**
     * Tom liste betyr «ingen lagret preferanse» — altså katalogens standard. Det er den samme
     * tilstanden som en manglende rad, og klienten trenger derfor bare å kjenne den ene.
     */
    columns: z.array(tableColumnPrefSchema),
  })
  .meta({ id: 'TablePrefsDto' });
export type TablePrefsDto = z.infer<typeof tablePrefsDtoSchema>;

export const putTablePrefsRequestSchema = z
  .object({ columns: z.array(tableColumnPrefSchema).max(MAX_TABLE_COLUMNS) })
  .meta({ id: 'PutTablePrefsRequest' });
export type PutTablePrefsRequest = z.infer<typeof putTablePrefsRequestSchema>;

/**
 * TABELLNØKKELEN ER EN RUTEPARAMETER, OG DERFOR HAR DEN ET SKJEMA.
 *
 * `ck_user_table_prefs__table_key` slipper 1–60 tegn igjennom, og alt over det blir en
 * databasefeil framfor en 400. Formen er strammet inn utover lengden av samme grunn som
 * slug-ene ellers i huset: nøkkelen står i en URL, den er kodens og ikke brukerens, og den
 * eneste grunnen til at en annen form skulle dukke opp er at noen skrev den feil.
 */
export const tableKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9-]*$/, 'A table key is lower-case letters, digits and hyphens.');

export const tablePrefsParamsSchema = z
  .object({ tableKey: tableKeySchema })
  .meta({ id: 'TablePrefsParams' });
export type TablePrefsParams = z.infer<typeof tablePrefsParamsSchema>;
